import type { WineListRenderItem } from '../types/venue';
import { compareWineColors } from './wineColor';

/**
 * Sort modes shared by the admin Preview panel and the customer-facing
 * /m/preview-mock prototype.
 *
 * "Default order" groups by wine color in the natural reading sequence
 * (sparkling → white → rosé → red → sweet → fortified → other) and falls
 * back to the server-supplied position within each color group. This makes
 * a long mixed list scan like a printed wine list out of the box; admins
 * can always override with one of the explicit sort modes.
 */
export type WineListSortMode =
  | 'DEFAULT'
  | 'PRICE_ASC'
  | 'PRICE_DESC'
  | 'VINTAGE_YOUNG_FIRST'
  | 'VINTAGE_OLD_FIRST'
  | 'COLOR_WHITE_FIRST'
  | 'COLOR_RED_FIRST';

/**
 * Display labels for the sort dropdown. Kept here so admin and prototype UIs
 * stay perfectly aligned; if you rename a label, both surfaces update.
 */
export const WINE_LIST_SORT_LABELS: Record<WineListSortMode, string> = {
  DEFAULT: 'Default order',
  PRICE_ASC: 'Price: Low to High',
  PRICE_DESC: 'Price: High to Low',
  VINTAGE_YOUNG_FIRST: 'Vintage: Young to Old',
  VINTAGE_OLD_FIRST: 'Vintage: Old to Young',
  COLOR_WHITE_FIRST: 'Color: White to Red',
  COLOR_RED_FIRST: 'Color: Red to White',
};

/**
 * Canonical wine-color sequence (Sparkling → White → Orange → Rosé → Red →
 * Sweet → Fortified) is owned by `compareWineColors` in `./wineColor`. Both
 * the DEFAULT sort and the explicit COLOR_* sort modes derive their ordering
 * from that one comparator — no inline rank table here.
 */

/**
 * Resolve the bottle price for sorting. Prefer `bottlePrice` (W3+ canonical
 * field) and fall back to the legacy `price` alias so both old and new
 * payloads sort correctly.
 */
const bottlePriceOf = (item: WineListRenderItem): number | null =>
  item.bottlePrice ?? item.price ?? null;

/**
 * Parse `displayVintage` into a numeric year. Non-numeric (NV / MV / blank)
 * returns null and sorts to the end of the list in either vintage direction.
 */
const vintageYearOf = (item: WineListRenderItem): number | null => {
  if (!item.vintage) return null;
  const n = parseInt(item.vintage, 10);
  return Number.isFinite(n) ? n : null;
};

/**
 * Generic comparator that always pushes nulls to the end, and falls back to
 * a numeric comparison in the requested direction for non-null pairs.
 */
const compareNullableNumber = (
  a: number | null,
  b: number | null,
  ascending: boolean,
): number => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return ascending ? a - b : b - a;
};

/**
 * Pure sort. Never mutates the input array.
 *
 * "Default order" groups by color in natural reading sequence (sparkling →
 * white → rosé → red → sweet → fortified → other), with stable ordering
 * inside each color group so the server-supplied position is preserved as
 * the secondary key.
 */
export const sortWineListItems = (
  items: WineListRenderItem[],
  mode: WineListSortMode,
): WineListRenderItem[] => {
  if (mode === 'DEFAULT') {
    // JS Array.prototype.sort is stable since ES2019, so equal-rank items
    // keep their input order — i.e. the server's section/position order.
    return [...items].sort((a, b) => compareWineColors(a.color, b.color));
  }

  const arr = [...items];
  switch (mode) {
    case 'PRICE_ASC':
      arr.sort((a, b) => compareNullableNumber(bottlePriceOf(a), bottlePriceOf(b), true));
      return arr;
    case 'PRICE_DESC':
      arr.sort((a, b) => compareNullableNumber(bottlePriceOf(a), bottlePriceOf(b), false));
      return arr;
    case 'VINTAGE_OLD_FIRST':
      arr.sort((a, b) => compareNullableNumber(vintageYearOf(a), vintageYearOf(b), true));
      return arr;
    case 'VINTAGE_YOUNG_FIRST':
      arr.sort((a, b) => compareNullableNumber(vintageYearOf(a), vintageYearOf(b), false));
      return arr;
    case 'COLOR_WHITE_FIRST':
      // Canonical order ascending: Sparkling → White → Orange → Rosé → Red → Sweet → Fortified.
      arr.sort((a, b) => compareWineColors(a.color, b.color));
      return arr;
    case 'COLOR_RED_FIRST':
      // Canonical order reversed.
      arr.sort((a, b) => compareWineColors(b.color, a.color));
      return arr;
  }
};
